import {effect, inject, Injectable} from '@angular/core';
import {environment} from '../../../../environments/environment';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable, Subject} from 'rxjs';
import {AuthenticateResponse} from '../model/auth/authenticate-response';
import {tap} from 'rxjs/operators';
import {PlayerAuthenticationService} from './player-authentication.service';
import {PlayerProfile} from '../model/player-profile';
import {PlayerDashboard} from '../model/player-dashboard';
import {Achievement} from '../model/achievement';
import {Pagination} from '../../../model/pagination';
import {StatisticFilter} from '../model/api/StatisticFilter';
import {StatisticMatch} from '../model/statistic-match';
import {PagingFilter} from '../../shared/model/paging-filter';
import {PlayerProfileEvent} from '../model/player-profile-event';
import {PlayerProfileServerSettings} from '../model/player-profile-server-settings';
import {Payment} from '../model/payment';
import {TranslateService} from '@ngx-translate/core';
import {PlayerAccount} from '../model/player-account';
import {DateHelper} from '../../../helper/date.helper';
import {DownloadService} from '../../../services/download.service';
import {RestErrorHelper} from '../../../helper/rest-error-helper';
import {FrontendNotificationService} from '../../frontend/service/frontend-notification.service';
import {MessagesFilter} from '../model/api/MessagesFilter';
import {PlayerMessage} from '../model/player-message';
import {
  PlayerProfileUserFilterType
} from '../../backend/admin/player-profile/player-profile-users/PlayerProfileUserFilterType';
import {FlutterService} from '../../../services/app/flutter.service';
import {SubsriptionStatus} from '../model/app/subsription-status';
import {TeamsLeaguesResponse} from '../model/api/teams-leagues-response';
import {TeamLeague} from '../model/api/team-league';
import {CalendarItemEvent} from '../model/calendar-item-event';
import {PlayerPictureReport} from '../model/player-picture-report';

@Injectable(
  {
    providedIn: 'root'
  }
)
export class PlayerProfileService
{
  private http = inject(HttpClient);
  public playerAuthenticationService = inject(PlayerAuthenticationService);
  private frontendNotificationService = inject(FrontendNotificationService);
  private translate = inject(TranslateService);
  private restErrorHelper = inject(RestErrorHelper);
  private flutterService = inject(FlutterService);

  private postingInAppPurchase = false;

  public onInAppPurchaseComplete = new Subject<any>();
  inAppPurchaseComplete$ = this.onInAppPurchaseComplete.asObservable();

  public onCountPictureReports = new Subject<any>();
  countPictureReport$ = this.onCountPictureReports.asObservable();

  private lastPayment: Payment;
  private inAppSubscriptionAttempt = 0;
  private successFullInAppTransactionId: string;

  constructor()
  {
    effect(() => {
      this.tokenReceived(this.frontendNotificationService.fcmToken());
    });
    effect(() => {
      this.subscriptionStatusChanged(this.frontendNotificationService.subscriptionStatus());
    });
  }

  public static isActive(): boolean
  {
    // return environment.local || environment.test2k;
    return true;
  }

  private tokenReceived(fcmToken: string): void
  {
    console.log('tokenReceived: ' + fcmToken);
    if (fcmToken)
    {
      const playerProfile = this.playerAuthenticationService.playerSessionService.playerProfileObject;
      if (playerProfile.fcmToken !== fcmToken)
        this.updateFcmToken(fcmToken);
    }
  }

  public updateFcmToken(fcmToken: string): void
  {
    if (!fcmToken)
      return;

    const body = {fcmToken};
    this.updateProfileField(body, true).subscribe({
      next: data =>
      {
        console.log('fcmToken erfolgreich auf dem Server aktualisiert', data);
        this.flutterService.addDebugText('fcmToken erfolgreich auf dem Server aktualisiert');
      },
      error: error => console.log('fcmToken nicht aktualisiert (keine Änderung)', error)
    });
  }

  loadSettings(): Observable<PlayerProfileServerSettings>
  {
    const url = environment.baseUrlPlayerProfile + 'server/settings';
    console.log('HTTP GET: ' + url);
    return this.http.get<PlayerProfileServerSettings>(url);
  }

  registerNewPlayer(body: any): Observable<AuthenticateResponse>
  {
    const url = environment.baseUrlPlayerProfile + 'authentication/register';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<AuthenticateResponse>(url, body).pipe(
      tap(data =>
      {
        if (data.token)
          this.playerAuthenticationService.loginSuccess(data);
      })
    );
  }

  login(body: any): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'authentication/login';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<AuthenticateResponse>(url, body).pipe(
      tap(data =>
      {
        if (data.token && !data.errorMsg)
        {
          this.initPlayerProfile(data.playerProfile, 0,  true);
          this.playerAuthenticationService.loginSuccess(data);
        }
      })
    );
  }

  logout(): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'authentication/logout';
    console.log('HTTP POST: ' + url);
    return this.http.post<AuthenticateResponse>(url, undefined).pipe(
      tap(() =>
      {
        this.playerAuthenticationService.logoutSuccess();
      })
    );
  }

  changeMailAddress(mail: string): Observable<any>
  {
    const body = {mail};
    const url = environment.baseUrlPlayerProfile + 'account/changeMail';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body);
  }

  public changePassword(password: string, passwordOld: string): Observable<any>
  {
    const body = {
      password,
      passwordOld
    };
    const url = environment.baseUrlPlayerProfile + 'account/changepassword';
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, body);
  }

  public passwordForgotten(mail: string): Observable<any>
  {
    const body = { mail };
    const url = environment.baseUrlPlayerProfile + 'authentication/forgotpassword';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body);
  }
  public passwordForgottenChange(mail: string, password: string, code: string): Observable<any>
  {
    const body = { code, mail, password };
    const url = environment.baseUrlPlayerProfile + 'authentication/changepassword';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body);
  }

  deletePlayerProfile(body: any): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/delete';
    console.log('HTTP DELETE: ' + url);
    return this.http.post<any>(url, body);
  }

  private initPlayerProfile(data: PlayerProfile, attempt: number, isAuthenticating?: boolean): void
  {
    if (data)
    {
      if (!data.pictureUrl && data.pictureFilename && data.playerNumber)
        data.pictureUrl = environment.baseUrlPlayerProfile + 'profile-images/' + data.pictureFilename;
      if (data.registrationPinValidTo)
        data.registrationPinValidTo = new Date(data.registrationPinValidTo);

      this.playerAuthenticationService.playerSessionService.playerProfileObject = data;
      if (data.lastPayment)
        this.lastPayment = data.lastPayment;

      if (!isAuthenticating)
      {
        this.checkTokenPaymentTo(data.account);
        // TODO this.checkPlatform(data.account);  -> Speichert gleichzeitig am Objekt PlayerProfile/Account (CannotAcquireLockException)
        // TODO Lösung: Daten von checkTokenPaymentTo, this.checkPlatform und this.updateFcmToken(fcmToken) gleichzeitig schicken oder zeitverzögert schicken

        if (this.frontendNotificationService.runsInApp())
        {
          const fcmToken = this.frontendNotificationService.fcmToken();
          if (data.fcmToken !== fcmToken)
            this.updateFcmToken(fcmToken);
        }
      }

      // Play Store Einschränkungen (Google/Apple Testaccount)
      // data.hidePayments = data.playerNumber === '8XCY';
      // console.log('initPlayerProfile, hidePayments: ' + data.hidePayments);
    }
  }

  private checkPlatform(account: PlayerAccount): void
  {
    if (!account || !this.playerAuthenticationService.isAuthenticated())
      return;

    const system = this.frontendNotificationService.system();
    console.log('checkPlatform, system: ' + system + ', account: ' + account.platform);
    if (system && account.platform !== system)
    {
      console.log('Plattform (' + system + ') unterscheidet sich von Account (' + account.platform + ')');
      const body = {platform: system};
      this.updateAccountField(body, true).subscribe({
        next: data =>
        {
          console.log('Plattform erfolgreich auf dem Server aktualisiert: ' + system, data);
          this.flutterService.addDebugText('Plattform erfolgreich auf dem Server aktualisiert: ' + system);
          account.platform = system;
        },
        error: error => console.log('Plattform nicht aktualisiert', error)
      });
    }
  }

  private checkTokenPaymentTo(account: PlayerAccount): void
  {
    if (!account || !account.paymentDateTo || !this.playerAuthenticationService.isAuthenticated())
      return;

    const paidToToken = this.playerAuthenticationService.paidTo;
    const paidTo = new Date(account.paymentDateTo);
    console.log('Payment Date To: ' + paidTo + ', paidTo (token): ' + paidToToken);
    if (!DateHelper.datesEqual(paidTo, paidToToken))
    {
      console.log('Payment To stimmt nicht mit Wert aus Token überein, Refresh Token...');
      const rt = this.playerAuthenticationService.refreshToken;
      if (rt)
        this.refreshToken(rt);
    }
  }

  /*public inAppSuccess(data: ReceiveProStatus): void
  {
    this.postingInAppPurchase = true;
    const body: SubsriptionStatus = {
      isActive: true,
      originalTransactionId: data.originalTransactionId,
      expiresDate: data.expiresDate,

      currencyCode: '',
      description: '',
      price: '',
      productId: '',
      rawPrice: 0,
      title: '',
      willAutoRenew: false

    };
    this.postInAppPurchase(subscriptionStatus).subscribe({
      next: (data: PlayerProfile) =>
      {
        console.log('Post InAppKauf erfolgreich');
        this.postingInAppPurchase = false;
        this.initPlayerProfile(data, attempt);
      },
      error: error =>
      {
        this.postingInAppPurchase = false;
        this.restErrorHelper.handleErrorService(error);
      }
    });
  }*/

  private subscriptionStatusChanged(subscriptionStatus: SubsriptionStatus): void
  {
    console.log('subscriptionStatusChanged: ', subscriptionStatus);
    if (this.lastPayment)
      this.checkInAppPurchase(this.lastPayment);
  }

  public checkInAppPurchaseProfile(): void
  {
    if (this.lastPayment)
      this.checkInAppPurchase(this.lastPayment);
  }

  private checkInAppPurchase(lastPayment: Payment): void
  {
    if (lastPayment?.vendorCd === 'MOLLIE' || this.frontendNotificationService.system() !== 'ios')
      return;

    this.inAppSubscriptionAttempt++;
    const subscriptionStatus = this.frontendNotificationService.subscriptionStatus();
    console.log('checkInAppPurchase, attempt: ' + this.inAppSubscriptionAttempt + ', postingActive: ' +
      this.postingInAppPurchase + ', subscriptionStatus, lastPayment', subscriptionStatus, lastPayment);

    this.flutterService.addDebugText('checkInAppPurchase, attempt: ' + this.inAppSubscriptionAttempt +
      ', active: ' + subscriptionStatus?.isActive +
      ', expiresDate: ' + subscriptionStatus?.expiresDate +
      ', originalTransactionId: ' + subscriptionStatus?.originalTransactionId +
      ', lastPayment: ' + JSON.stringify({id: lastPayment.id, paymentDate: lastPayment.paymentDate,
        paymentDateTo: lastPayment.paymentDateTo, methodCd: lastPayment.methodCd, orderId: lastPayment.orderId}));


    if (!this.postingInAppPurchase &&
      this.inAppSubscriptionAttempt < 5 &&
      this.successFullInAppTransactionId !== subscriptionStatus.originalTransactionId &&
      subscriptionStatus?.isActive &&
      subscriptionStatus?.expiresDate &&
      this.isInAppPurchaseDifferent(subscriptionStatus, lastPayment))
    {
      console.log('checkInAppPurchase: Success');
      this.flutterService.addDebugText('checkInAppPurchase: Success');

      this.postingInAppPurchase = true;
      this.postInAppPurchase(subscriptionStatus).subscribe({
        next: (data: PlayerProfile) =>
        {
          console.log('Post InAppKauf erfolgreich', data);
          this.postingInAppPurchase = false;
          this.successFullInAppTransactionId = subscriptionStatus.originalTransactionId;

          if (data?.id)
          {
            // Daten aktualisieren (Direktes Anwenden neuer Zahlungen)
            this.flutterService.addDebugText('postInAppPurchase: Erfolg');
            this.onInAppPurchaseComplete.next(true); // TODO Hier wird wieder loadData aufgerufen -> Schleife möglich
          }
          else this.flutterService.addDebugText('postInAppPurchase: Keine Änderungen');
          // window.location.reload(); // Seite aktualisieren
        },
        error: error =>
        {
          this.postingInAppPurchase = false;
          this.restErrorHelper.handleErrorService(error);   // TODO später rausnehmen?
        }
      });
    }
    else console.log('checkInAppPurchase, kein Update nötig');
  }

  private isInAppPurchaseDifferent(subscriptionStatus: SubsriptionStatus, lastPayment: Payment): boolean
  {
    // if (subscriptionStatus && lastPayment && lastPayment.vendorCd === 'APPLE')
    if (subscriptionStatus && lastPayment)
    {
      if (subscriptionStatus.originalTransactionId && subscriptionStatus.originalTransactionId !== lastPayment.orderId)
      {
        console.log('isInAppPurchaseDifferent: true (originalTransactionId is different)');
        return true;
      }

      if (subscriptionStatus.expiresDate && lastPayment.paymentDateTo)
      {
        if (DateHelper.datesEqual(new Date(subscriptionStatus.expiresDate), new Date(lastPayment.paymentDateTo)))
        {
          console.log('isInAppPurchaseDifferent: false (dates are not different: ' +
            subscriptionStatus.expiresDate + ' | ' + lastPayment.paymentDateTo + ')');
          return false;
        }
        else if (new Date(subscriptionStatus.expiresDate).getDate() < new Date(lastPayment.paymentDateTo).getDate())
        {
          console.log('isInAppPurchaseDifferent: false (subscription expires before payment date)');
          return false;
        }
        else
        {
          console.log('isInAppPurchaseDifferent: true (dates are different)');
          return true;
        }
      }
      else
      {
        console.log('isInAppPurchaseDifferent: true (dates missing)');
        return true;
      }
    }
    console.log('isInAppPurchaseDifferent: false (missing data)');
    return false;
  }

  private postInAppPurchase(body: SubsriptionStatus): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/inAppPurchase';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body);
  }

  private refreshToken(refreshToken: string): void
  {
    this.refreshTokenService(refreshToken).subscribe({
      next: (response: PlayerProfile) => console.log('refreshToken', response),
      error: error => console.log('Fehler bei "refreshToken"', error)
    });
  }

  private refreshTokenService(refreshToken: string): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'profile';
    const headers = new HttpHeaders({
      'Refresh-Token': refreshToken
    });
    return this.http.get<PlayerProfile>(url, {headers});
  }

  loadProfileData(): Observable<PlayerProfile>
  {
    const url = environment.baseUrlPlayerProfile + 'profile';
    console.log('HTTP GET: ' + url);
    return this.http.get<PlayerProfile>(url).pipe(tap((data: PlayerProfile) =>
      {
        this.initPlayerProfile(data, 0);
      })
    );
  }
  updateProfileField(body: any, result?: boolean): Observable<PlayerProfile>
  {
    let url = environment.baseUrlPlayerProfile + 'profile/update';
    if (!result)
      url += '?noresult=1';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<PlayerProfile>(url, body).pipe(tap((data: PlayerProfile) =>
      {
        if (result && data)
          this.playerAuthenticationService.playerSessionService.playerProfileObject = data;
      })
    );
  }
  uploadProfileImage(body: FormData): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/image';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body);
  }
  deleteProfileImage(): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/image';
    console.log('HTTP DELETE: ' + url);
    return this.http.delete<any>(url);
  }

  updateAccountField(body: any, result?: boolean): Observable<PlayerAccount>
  {
    let url = environment.baseUrlPlayerProfile + 'account/update';
    if (!result)
      url += '?noresult=1';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<PlayerAccount>(url, body);
  }

  loadDashboardData(): Observable<PlayerDashboard>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/dashboard';
    console.log('HTTP GET: ' + url);
    return this.http.get<PlayerDashboard>(url).pipe(tap((data: PlayerDashboard) =>
      {
        this.initPlayerProfile(data.playerProfile, 0);
      })
    );
  }

  getCalendar(): Observable<PlayerProfileEvent[]>
  {
    const url = environment.baseUrlPlayerProfile + 'calendar';
    console.log('HTTP GET: ' + url);
    return this.http.get<PlayerProfileEvent[]>(url).pipe(tap((data: PlayerProfileEvent[]) =>
      {
        for (const row of data)
          PlayerProfileEvent.initData(row);
      })
    );
  }

  getCalendarEvents(): Observable<CalendarItemEvent[]>
  {
    const url = environment.baseUrlPlayerProfile + 'calendar/events';
    console.log('HTTP GET: ' + url);
    return this.http.get<CalendarItemEvent[]>(url).pipe(tap((data: any) =>
      {
        if (data)
        {
          for (const row of data)
            CalendarItemEvent.initData(row, this.translate);
        }
      })
    );
  }

  public getUserSettings(): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/settings';
    console.log('HTTP GET: ' + url);
    return this.http.get<any>(url);
  }
  public saveUserSettings(body: any): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/settings';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body).pipe(tap((data: any) =>
      {
        if (body?.length > 0)
        {
          for (const item of body)
          {
            /*if (item.settingCd === PLAYER_PROFILE_SETTING_NOTIFIY_NEARBY_ACTIVE)
              this.playerAuthenticationService.playerSessionService.eventNotificationActive = item.value === '1';
            else if (item.settingCd === PLAYER_PROFILE_SETTING_NOTIFIY_NEARBY_RADIUS)
              this.playerAuthenticationService.playerSessionService.eventNotificationRadius = item.value;*/
          }
        }
      })
    );
  }

  public getLinkedLeaguePlayers(): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/playerIdentification?typeCd=LEA';
    console.log('HTTP GET: ' + url);
    return this.http.get<any>(url);
  }
  public linkPlayer(body: any): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/playerIdentification';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body);
  }
  public deletePlayerIdentification(body: any): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/playerIdentification/delete';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body);
  }

  public deregisterFromEvent(eventId: number): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'calendar/deregister/event/' + eventId;
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, undefined);
  }

  public getLinkedLeagues(): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/league';
    console.log('HTTP GET: ' + url);
    return this.http.get<any>(url);
  }
  public getPossiblePlayerLeagueTeams(): Observable<TeamsLeaguesResponse>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/league/team';
    console.log('HTTP GET: ' + url);
    return this.http.get<TeamsLeaguesResponse>(url);
  }
  public linkPlayerTeamLeague(body: TeamLeague): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/league/team/link';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body);
  }
  public activateOrDeactivateTeamLeagueCalendarSync(playerProfileEventId: number, activate: boolean): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/league/team/' + playerProfileEventId +
      (activate ? '/activateSync' : '/deactivateSync');
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, undefined);
  }
  public removePlayerLeague(playerProfileEventId: number): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/league/' + playerProfileEventId;
    console.log('HTTP DELETE: ' + url);
    return this.http.delete<any>(url);
  }

  getPlayerAchievements(): Observable<Achievement[]>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/achievement';
    console.log('HTTP GET: ' + url);
    return this.http.get<Achievement[]>(url);
  }

  getStatisticsMatches(paging: PagingFilter, statisticFilter: StatisticFilter): Observable<Pagination>
  {
    const body = {
      paging,
      statisticFilter
    };
    const url = environment.baseUrlPlayerProfile + 'statistics/match/page';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<Pagination>(url, body).pipe(
      tap(data =>
      {
        if (data?.content?.length > 0)
        {
          for (const sm of data.content)
            StatisticMatch.init(sm);
        }
      })
    );
  }

  deleteStatisticsMatch(database: number, matchId: number): Observable<any>
  {
    const body = {database, matchId};
    const url = environment.baseUrlPlayerProfile + 'statistics/match/delete';
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, body);
  }

  getStatisticsTable(statisticFilter: StatisticFilter): Observable<any>
  {
    const body = {
      statisticFilter
    };
    const url = environment.baseUrlPlayerProfile + 'statistics/table';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<Pagination>(url, body);
  }

  getStatisticsChart(statisticFilter: StatisticFilter): Observable<any>
  {
    const body = {
      statisticFilter
    };
    const url = environment.baseUrlPlayerProfile + 'statistics/chart';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<Pagination>(url, body);
  }

  public getPayments(): Observable<Payment[]>
  {
    const url = environment.baseUrlPlayerProfile + 'account/payment';
    console.log('HTTP GET: ' + url);
    return this.http.get<Payment[]>(url).pipe(
      tap(data =>
      {
        if (data?.length > 0)
        {
          for (const item of data)
            Payment.init(item, this.translate);
        }
      })
    );
  }

  public deletePayment(paymentId: number): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'account/payment/' + paymentId;
    console.log('HTTP DELETE: ' + url);
    return this.http.delete<any>(url);
  }

  public purchaseProAccount(): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'account/payment/purchase';
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, undefined);
  }

  /*public testGetPayment(paymentId: string): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'account/payment/test/' + paymentId;
    console.log('HTTP GET: ' + url);
    return this.http.get<any>(url, undefined);
  }*/

  public downloadInvoice(paymentId: number, filename: string, item: any): void
  {
    const httpOptions = {responseType: 'blob' as 'json'};

    const url = environment.baseUrlPlayerProfile + 'account/payment/' + paymentId + '/invoice';
    console.log('HTTP GET: ' + url);
    this.http.get(url, httpOptions).subscribe({
      next: data =>
      {
        console.log('Dokument erstellt: ', data);
        DownloadService.downloadFile(data, filename);
        item.customButtonDisabled = false;
      },
      error: error =>
      {
        item.customButtonDisabled = false;
        this.restErrorHelper.handleJsonBlobResponse(error);
      }
    });
  }

  public getAdminUsersPaged(filters: PlayerProfileUserFilterType): Observable<Pagination>
  {
    const url = environment.baseUrlPlayerProfile + 'admin/user/page';
    console.log('HTTP POST: ' + url, filters);
    return this.http.post<Pagination>(url, filters).pipe(
      tap(data =>
      {
        if (data.content)
        {
          for (const user of data.content)
            this.initPlayerProfileUser(user);
        }
      })
    );
  }
  private initPlayerProfileUser(item: any): void
  {
    if (item.playerProfile?.birthday)
      item.playerProfile.birthday = new Date(item.playerProfile.birthday);
  }

  public getAdminPictureReports(showAll: boolean): Observable<any[]>
  {
    const url = environment.baseUrlPlayerProfile + 'admin/report/picture' + (showAll ? '?all=1' : '');
    console.log('HTTP GET: ' + url);
    return this.http.get<any[]>(url).pipe(
      tap(data =>
      {
        if (data?.length > 0)
        {
          for (const sm of data)
            PlayerPictureReport.init(sm);
        }
      })
    );
  }
  public getAdminPictureReportsCount(): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'admin/report/picture/count';
    console.log('HTTP GET: ' + url);
    return this.http.get<any>(url);
  }
  public verifyReportedPictureAdmin(body: any): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'admin/report/picture/verify';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body);
  }
  public reportPicture(body: any): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'report/picture';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body);
  }

  public updateAdminUserField(body: any): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'admin/user/update';
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, body);
  }
  public updateAdminPlayerProfileField(body: any): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'admin/playerProfile/update';
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, body);
  }
  public adminRenewProVersion(body: any): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'admin/playerProfile/renewPro';
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, body);
  }
  public syncMissingPlayerMatches(body: any): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'admin/playerProfile/syncMissing';
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, body);
  }

  public testPushNotification(): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/pushnotification/test';
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, undefined);
  }

  public getMessages(paging: PagingFilter, filter: MessagesFilter): Observable<Pagination>
  {
    const body = {
      paging,
      messageFilter: filter
    };
    const url = environment.baseUrlPlayerProfile + 'message/page';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<Pagination>(url, body).pipe(
      tap(data =>
      {
        if (data?.content?.length > 0)
        {
          for (const sm of data.content)
            PlayerMessage.init(sm);
        }
      })
    );
  }
  public deleteMessage(messageId: number): Observable<any>
  {
    const url = environment.baseUrlPlayerProfile + 'message/' + messageId;
    console.log('HTTP DELETE: ' + url);
    return this.http.delete<any>(url);
  }

  public impersonatePlayerProfileUser(accountId: number): void
  {
    if (!accountId)
      return;

    this.impersonateService(accountId).subscribe({
      next: () =>
      {
        window.open('/frontend/player', '_blank');
      },
      error: error => this.restErrorHelper.handleErrorService(error)
    });
  }

  private impersonateService(accountId: number): Observable<any>
  {
    const body = {accountId};
    const url = environment.baseUrlPlayerProfile + 'admin/user/impersonate';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body).pipe(
      tap(data =>
      {
        console.log('Impersonate Login Response: ', data);
        if (data?.token && !data.errorMsg)
        {
          this.initPlayerProfile(data.playerProfile, 0,  true);
          this.playerAuthenticationService.loginSuccess(data);
        }
      })
    );
  }

  public generateRegistrationCode(): Observable<PlayerProfile>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/registrationcode/generate';
    console.log('HTTP POST: ' + url);
    return this.http.post<PlayerProfile>(url, undefined).pipe(tap((data: PlayerProfile) =>
      {
        this.initPlayerProfile(data, 0);
      })
    );
  }

  public deleteRegistrationCode(): Observable<PlayerProfile>
  {
    const url = environment.baseUrlPlayerProfile + 'profile/registrationcode/delete';
    console.log('HTTP POST: ' + url);
    return this.http.post<PlayerProfile>(url, undefined).pipe(tap((data: PlayerProfile) =>
      {
        this.initPlayerProfile(data, 0);
      })
    );
  }

  public useRegistrationCode(body: any): Observable<PlayerProfile>
  {
    const url = environment.baseUrlPlayerProfile + 'frontend/registrationcode/use';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<PlayerProfile>(url, body);
  }
}
